import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, Calculator, ListChecks, IndianRupee, Trophy,
  Layers, Sparkles, Loader2, Code2, Play
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ICON_MAP = {
  "calculator": Calculator,
  "list-checks": ListChecks,
  "indian-rupee": IndianRupee,
  "trophy": Trophy,
};

function ProjectCard({ project, onOpen }) {
  const Icon = ICON_MAP[project.icon] || Code2;
  return (
    <motion.button
      whileHover={{ y: -6 }}
      transition={{ duration: 0.2 }}
      onClick={onOpen}
      data-testid={`showcase-card-${project.project_id}`}
      className="group relative bg-zinc-900/60 border border-zinc-800 rounded-2xl overflow-hidden text-left hover:border-zinc-700 transition-colors"
      style={{ ['--accent']: project.color }}
    >
      {/* Gradient glow on hover */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        style={{ background: `radial-gradient(600px circle at top right, ${project.color}15, transparent 60%)` }}
      />

      <div className="relative p-7">
        <div className="flex items-start justify-between mb-5">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${project.color}1A`, color: project.color }}
          >
            <Icon size={26} />
          </div>
          <span className="text-[10px] font-mono font-bold text-zinc-500 bg-zinc-800/80 px-2 py-1 rounded">
            {project.step_count} STEPS
          </span>
        </div>

        <h3 className="font-outfit font-bold text-2xl text-white mb-2">{project.name}</h3>
        <p className="text-sm text-zinc-400 font-dm-sans leading-relaxed mb-5 line-clamp-2">{project.tagline}</p>

        <div className="flex items-center gap-1.5 flex-wrap mb-5">
          {project.skills.slice(0, 4).map((s) => (
            <span key={s} className="text-[10px] font-dm-sans text-zinc-300 bg-zinc-800/70 px-2 py-0.5 rounded-full">
              {s}
            </span>
          ))}
        </div>

        <div
          className="inline-flex items-center gap-1.5 text-sm font-outfit font-bold transition-transform"
          style={{ color: project.color }}
        >
          Try it live
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </motion.button>
  );
}

export default function ProjectShowcasePage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Python Project Showcase — Try Live in Your Browser | Python Lab";
    axios.get(`${API}/python-lab/showcase/projects`)
      .then((r) => setProjects(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-zinc-900 sticky top-0 z-30 bg-[#050505]/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="showcase-home-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-dm-sans transition-colors"
          >
            <ArrowLeft size={16} /> Home
          </button>
          <div className="flex items-center gap-3">
            <button
              data-testid="showcase-cta-signup"
              onClick={() => navigate("/ai-tools/python-lab")}
              className="hidden sm:inline-flex items-center gap-1.5 bg-white text-black hover:bg-zinc-100 px-4 py-1.5 rounded-md text-xs font-dm-sans font-bold transition-colors"
            >
              <Sparkles size={12} /> Open Python Lab
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 md:py-20">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-full px-3 py-1 text-xs font-mono text-[#00F0FF] mb-6">
            <Layers size={12} /> PROJECT SHOWCASE
          </div>
          <h1 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-5">
            Real Python projects.<br />
            <span className="bg-gradient-to-r from-[#00F0FF] via-[#3DDC84] to-[#7000FF] bg-clip-text text-transparent">
              Run them right in your browser.
            </span>
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg font-dm-sans max-w-2xl mx-auto leading-relaxed">
            Click into any project below — the code is live, the editor is real, and you'll see Python execute in your browser. No sign-up to play. Sign up when you want to learn how to build them yourself.
          </p>
        </motion.div>

        {/* Project grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="text-[#00F0FF] animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {projects.map((p, i) => (
              <motion.div
                key={p.project_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
              >
                <ProjectCard project={p} onOpen={() => navigate(`/showcase/python-lab/${p.project_id}`)} />
              </motion.div>
            ))}
          </div>
        )}

        {/* CTA strip */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 bg-gradient-to-br from-[#00F0FF]/10 via-zinc-900/40 to-[#7000FF]/10 border border-zinc-800 rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#00F0FF]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#7000FF]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative">
            <h2 className="font-outfit font-black text-3xl sm:text-4xl text-white mb-4">
              Want to <span className="text-[#00F0FF]">build these yourself?</span>
            </h2>
            <p className="text-zinc-400 text-base font-dm-sans max-w-xl mx-auto leading-relaxed mb-7">
              Python Lab teaches you each step with auto-graded lessons and an AI tutor that nudges (never spoils) the answer. Start free.
            </p>
            <button
              data-testid="showcase-bottom-cta"
              onClick={() => navigate("/ai-tools/python-lab")}
              className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black px-6 py-3 rounded-lg text-sm font-outfit font-bold transition-colors"
            >
              <Play size={14} /> Open Python Lab
            </button>
          </div>
        </motion.section>
      </main>

      <footer className="border-t border-zinc-900 mt-12">
        <div className="max-w-6xl mx-auto px-6 py-6 text-center text-xs text-zinc-600 font-dm-sans">
          Python Lab · Powered by Pyodide (Python 3.12 in WebAssembly) · No installs required
        </div>
      </footer>
    </div>
  );
}
